<template>
  <div class="max-width">
    <div class="subs">
      <router-link to="/" class="subs__links">Home</router-link>
      <span>Themes</span>
    </div>

    <div class="themes-page">
      <h1 class="section__title">Pick a Theme <br>Built for Sales</h1>
      <div class="themes-page__text">
        Themes that do more than look good—they're built to drive revenue, simplify the shopping journey, and support real growth.
      </div>

      <div class="themes-page__tags">
        <button
            type="button"
            class="themes-page__tags-item"
            :class="{ 'themes-page__tags-item_active': selectedTag === 'all' }"
            @click="selectedTag = 'all'"
        >
          All Themes
        </button>
        <button
            v-for="tag in availableTags"
            :key="tag"
            type="button"
            class="themes-page__tags-item"
            :class="{ 'themes-page__tags-item_active': selectedTag === tag }"
            @click="selectedTag = tag"
        >
          {{ capitalize(tag) }}
        </button>
      </div>

      <div class="themes-page__sort">
        Sort by:
        <select v-model="sortOrder">
          <option value="popularity">Popularity</option>
          <option value="latest">Latest First</option>
          <option value="name">Name</option>
        </select>
      </div>

      <div class="themes-page__grid">
        <ThemeCard
            v-for="theme in filteredThemes"
            :key="theme.title"
            :theme-link="theme.themeLink"
            :theme-name="theme.themeName"
            :title="theme.title"
            :preview-link="theme.previewLink"
            :tag="theme.tag"
            :image="theme.image"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ThemeCard from '@/components/ThemeCard.vue'

const themes = ref([])
const selectedTag = ref('all')
const sortOrder = ref('popularity')

const listId = 'all-themes'

onMounted(async () => {
  const res = await fetch('/assets/json/themes.json')
  const data = await res.json()

  // НИЧЕГО НЕ ПЕРЕВОРАЧИВАЕМ — JSON уже в порядке "latest first"
  themes.value = data.flatMap(theme =>
      theme.styles
          .filter(style => !(style.hiddenIn?.includes(listId) || theme.hiddenIn?.includes(listId)))
          .map(style => ({
            ...style,
            themeLink: `/themes/${theme.id}`,
            themeName: theme.id
          }))
  )
})

const availableTags = computed(() => {
  const tagsSet = new Set(themes.value.map(theme => theme.tag.toLowerCase()))
  return Array.from(tagsSet)
})

const filteredThemes = computed(() => {
  let filtered = themes.value

  if (selectedTag.value !== 'all') {
    filtered = filtered.filter(theme => theme.tag.toLowerCase() === selectedTag.value)
  }

  if (sortOrder.value === 'name') {
    filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title))
  }

  if (sortOrder.value === 'latest') {
    // Просто возвращаем оригинальный порядок: mix → megastore
    filtered = themes.value
  }

  return filtered
})

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)
</script>

<style lang="scss">
.themes-page {
  .section__title {
    text-align: center;
    font-weight: 900;
    font-size: 5rem;
    line-height: 1;
    margin: 0 0 1.25rem;

    @include md {
      font-size:3.5rem;
    }

    @include sm {
      font-size:2.5rem;
    }
  }

  &__text {
    margin: 0 auto 2.5rem;
    max-width: 730px;
    text-align: center;
    color: var(--color-gray);
    font-size: 1.5rem;
    line-height: 1.3;

    @include md {
      font-size:1.25rem;
      margin-bottom:1.5rem;
    }

    @include sm {
      font-size:1rem;
    }
  }

  &__tags {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    margin: 0 auto 3.5rem;
    max-width: 782px;

    &-item {
      cursor: pointer;
      appearance: none;
      border: 0;
      background: var(--color-accent-light);
      font-family: var(--font-family);
      color: var(--color-accent);
      height: 1.875rem;
      display: flex;
      text-transform:capitalize;
      align-items: center;
      padding: 0 .75rem 1px;
      font-size: .875rem;
      border-radius: 40px;
      transition: var(--transition);

      &_active,
      &:hover {
        background: var(--color-accent);
        color: #fff;
      }
    }
  }

  &__sort {
    text-align:right;
    font-size:.875rem;
    margin-bottom: 1.25rem;
    letter-spacing: -0.01em;

    select {
      font-size:.875rem;
      color:var(--color-accent);
      border:0;
      background:none;
      font-family:var(--font-family);
      cursor:pointer;
      outline:none;
      user-select:none;
      font-weight:500;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    grid-row-gap: 3rem;

    @include sm {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}
</style>
